/**
 * Configuration interface for network printing operations
 */
export interface INetworkPrintingConfig {
    /** IP address of the network printer */
    printerIP: string;
    /** ZPL (Zebra Programming Language) data to print */
    zplData: string;
    /** Port number for the printer connection (defaults to 5694) */
    port?: number;
}

/**
 * Default port for network printing operations
 */
export const DEFAULT_NETWORK_PRINTING_PORT = 6101;

/**
 * Configuration interface for querying printer DPI via network
 */
export interface IGetPrinterDPIConfig {
    /** IP address of the network printer */
    printerIP: string;
    /** Port number for the printer connection (defaults to DEFAULT_NETWORK_PRINTING_PORT) */
    port?: number;
}

/**
 * Result interface for printer DPI query
 */
export interface IGetPrinterDPIResult {
    /** Printer DPI value (e.g., 203, 300) */
    dpi: number;
}

/**
 * Validation helper functions for network printing configuration
 */
export class NetworkPrintingValidation {
    /**
     * Validates if the provided IP address is in a valid IPv4 format
     * @param ip The IP address to validate
     * @returns true if valid, false otherwise
     */
    public static isValidIPAddress(ip: string): boolean {
        if (!ip || typeof ip !== 'string') {
            return false;
        }

        const ipv4Regex = /^(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/;
        return ipv4Regex.test(ip);
    }

    /**
     * Validates if the provided ZPL data is non-empty
     * @param zplData The ZPL data to validate
     * @returns true if valid, false otherwise
     */
    public static isValidZplData(zplData: string): boolean {
        return !!(zplData && typeof zplData === 'string' && zplData.trim().length > 0);
    }

    /**
     * Validates if the provided port number is valid
     * @param port The port number to validate
     * @returns true if valid, false otherwise
     */
    public static isValidPort(port: number): boolean {
        return !!(port && typeof port === 'number' && Number.isInteger(port) && port >= 1 && port <= 65535);
    }
}
